import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Megaphone, Plus, X, Bell, AlertTriangle, Info, 
  CheckCircle, Clock, Trash2, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

import { API_URL } from '@/config/api';

const priorityConfig = {
  low: { color: 'bg-slate-100 text-slate-700', icon: Info, label: 'Low' },
  normal: { color: 'bg-blue-100 text-blue-700', icon: Bell, label: 'Normal' },
  high: { color: 'bg-amber-100 text-amber-700', icon: AlertTriangle, label: 'High' },
  urgent: { color: 'bg-red-100 text-red-700', icon: AlertTriangle, label: 'Urgent' }
};

const AnnouncementBanner = ({ announcements, onMarkRead }) => {
  const unreadAnnouncements = announcements.filter(a => !a.is_read);
  
  if (unreadAnnouncements.length === 0) return null;

  const highPriority = unreadAnnouncements.filter(a => a.priority === 'urgent' || a.priority === 'high');
  const toShow = highPriority.length > 0 ? highPriority[0] : unreadAnnouncements[0];
  const config = priorityConfig[toShow.priority] || priorityConfig.normal;
  const Icon = config.icon;

  return (
    <div className={`${config.color} p-4 rounded-lg mb-6 flex items-start gap-3`} data-testid="announcement-banner">
      <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <h4 className="font-semibold">{toShow.title}</h4>
          <Badge variant="secondary" className="text-xs">
            {toShow.created_by_name}
          </Badge>
        </div>
        <p className="text-sm">{toShow.content}</p>
        <p className="text-xs mt-2 opacity-70">
          {format(new Date(toShow.created_at), 'dd MMM yyyy, HH:mm')}
        </p>
      </div>
      <div className="flex items-center gap-2">
        {unreadAnnouncements.length > 1 && (
          <Badge variant="secondary">{unreadAnnouncements.length} announcements</Badge>
        )}
        <Button 
          size="sm" 
          variant="ghost"
          onClick={() => onMarkRead(toShow.announcement_id)}
        >
          <Eye className="w-4 h-4 mr-1" /> Dismiss
        </Button>
      </div>
    </div>
  );
};

const AnnouncementsManager = () => {
  const { user, selectedBusiness } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: '',
    content: '',
    priority: 'normal',
    target_roles: ['user', 'admin']
  });

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  useEffect(() => {
    fetchAnnouncements();
  }, [selectedBusiness]);

  const fetchAnnouncements = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/announcements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAnnouncements(data);
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
    }
  };

  const handleCreate = async () => {
    if (!newAnnouncement.title.trim() || !newAnnouncement.content.trim()) {
      toast.error('Please fill in title and content');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/announcements`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newAnnouncement)
      });
      
      if (response.ok) {
        toast.success('Announcement published!');
        setIsCreateOpen(false);
        setNewAnnouncement({ title: '', content: '', priority: 'normal', target_roles: ['user', 'admin'] });
        fetchAnnouncements();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create announcement');
      }
    } catch (error) {
      toast.error('Failed to create announcement');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (announcementId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/announcements/${announcementId}/read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setAnnouncements(prev => 
        prev.map(a => a.announcement_id === announcementId ? { ...a, is_read: true } : a)
      );
    } catch (error) {
      console.error('Error marking announcement as read:', error);
    }
  };

  const handleDelete = async (announcementId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/announcements/${announcementId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Announcement deleted');
        fetchAnnouncements();
      }
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  return (
    <div className="space-y-4" data-testid="announcements-manager">
      {/* Banner for users */}
      <AnnouncementBanner announcements={announcements} onMarkRead={handleMarkRead} />

      {/* Admin management */}
      {isAdmin && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-cyan-500" />
              Announcements
            </CardTitle>
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-gradient-to-r from-cyan-500 to-blue-600">
                  <Plus className="w-4 h-4 mr-2" /> New Announcement
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Announcement</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      placeholder="Announcement title..."
                      className="mt-1.5"
                      value={newAnnouncement.title}
                      onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="content">Message</Label>
                    <Textarea
                      id="content"
                      placeholder="Write your announcement..."
                      className="mt-1.5 min-h-[100px]"
                      value={newAnnouncement.content}
                      onChange={(e) => setNewAnnouncement({ ...newAnnouncement, content: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Select 
                      value={newAnnouncement.priority} 
                      onValueChange={(v) => setNewAnnouncement({ ...newAnnouncement, priority: v })}
                    >
                      <SelectTrigger className="mt-1.5">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button variant="outline" onClick={() => setIsCreateOpen(false)} className="flex-1">
                      Cancel
                    </Button>
                    <Button onClick={handleCreate} disabled={loading} className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600">
                      Publish
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {announcements.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Megaphone className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                <p>No announcements yet</p>
                <p className="text-sm">Create one to notify your team</p>
              </div>
            ) : (
              <div className="space-y-3">
                {announcements.map((announcement) => {
                  const config = priorityConfig[announcement.priority] || priorityConfig.normal;
                  const Icon = config.icon;
                  return (
                    <div 
                      key={announcement.announcement_id}
                      className={`p-4 rounded-lg border ${announcement.is_read ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-300'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${config.color}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium">{announcement.title}</h4>
                              <Badge className={config.color}>{config.label}</Badge>
                              {!announcement.is_read && (
                                <Badge className="bg-cyan-100 text-cyan-700">New</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-600 mt-1">{announcement.content}</p>
                            <p className="text-xs text-slate-400 mt-2">
                              {announcement.created_by_name} • {format(new Date(announcement.created_at), 'dd MMM yyyy, HH:mm')}
                            </p>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDelete(announcement.announcement_id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export { AnnouncementBanner };
export default AnnouncementsManager;
